import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { DEFAULT_COMPANY } from '../config/CompanyDetails';
import { jobUnits } from '../utils/jobUnits';
import { invoiceBalance, isInvoiceVoided } from '../utils/invoiceBalance';

// ============================================
// TAX INVOICE PDF — mirrors the QuotePDF proforma layout exactly.
// Shows the references block (Invoice #, Quote #, Job #, Proforma #),
// line items, totals, payment status/balance, bank details and a compact
// payment-terms line only (invoices carry no full T&Cs).
// ============================================

const styles = StyleSheet.create({
  page: {
    padding: 0,
    backgroundColor: '#FFFFFF',
    fontFamily: 'Helvetica',
    position: 'relative',
    minHeight: '100%',
  },
  header: {
    width: '100%',
    backgroundColor: '#0A1929',
    padding: 12,
    paddingBottom: 8,
    marginBottom: 8,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerLeft: {
    flexDirection: 'column',
  },
  headerLogo: {
    width: 120,
    height: 50,
    objectFit: 'contain',
  },
  headerDetails: {
    textAlign: 'right',
  },
  headerDetailText: {
    fontSize: 9,
    color: '#FFFFFF',
    marginBottom: 2,
  },
  quoteTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#0A1929',
    textAlign: 'center',
    marginBottom: 8,
    letterSpacing: 2,
  },
  content: {
    paddingHorizontal: 30,
    paddingTop: 30,
    paddingBottom: 50,
    zIndex: 1,
    flex: 1,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
    padding: 6,
    backgroundColor: '#F5F7FA',
    borderRadius: 4,
  },
  infoItem: {
    flexDirection: 'column',
  },
  infoLabel: {
    fontSize: 8,
    color: '#666666',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  infoValue: {
    fontSize: 11,
    color: '#0A1929',
    fontWeight: 'bold',
    marginTop: 2,
  },
  infoValuePaid: {
    fontSize: 11,
    color: '#00A86B',
    fontWeight: 'bold',
    marginTop: 2,
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: '#E8ECEF',
    marginVertical: 6,
  },
  routeBox: {
    marginVertical: 4,
    padding: 6,
    backgroundColor: '#F0F8FF',
    borderRadius: 4,
  },
  routeText: {
    fontSize: 9,
    color: '#333333',
    marginBottom: 2,
  },
  twoCol: {
    flexDirection: 'row',
    gap: 20,
    marginBottom: 8,
  },
  col: {
    flex: 1,
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#0A1929',
    marginBottom: 5,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  sectionText: {
    fontSize: 9,
    color: '#333333',
    marginBottom: 1,
  },
  table: {
    marginVertical: 6,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#0A1929',
    padding: 8,
    borderRadius: 4,
  },
  tableHeaderText: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#FFFFFF',
    textTransform: 'uppercase',
  },
  tableRow: {
    flexDirection: 'row',
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#EEEEEE',
  },
  tableRowEven: {
    flexDirection: 'row',
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#EEEEEE',
    backgroundColor: '#F9FAFB',
  },
  colDescription: { flex: 3 },
  colQty: { flex: 1, textAlign: 'center' },
  colRate: { flex: 1, textAlign: 'right' },
  colAmount: { flex: 1.5, textAlign: 'right' },
  tableText: {
    fontSize: 9,
    color: '#333333',
  },

  totalsBox: {
    marginTop: 10,
    padding: 15,
    backgroundColor: '#F5F7FA',
    borderRadius: 4,
    alignItems: 'flex-end',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 3,
  },
  totalLabel: {
    fontSize: 9,
    color: '#666666',
    width: 100,
    textAlign: 'right',
  },
  totalValue: {
    fontSize: 9,
    color: '#0A1929',
    width: 100,
    textAlign: 'right',
    fontWeight: 'medium',
  },
  totalRowFinal: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 5,
    paddingTop: 5,
    borderTopWidth: 2,
    borderTopColor: '#0A1929',
  },
  totalLabelFinal: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#0A1929',
    width: 100,
    textAlign: 'right',
  },
  totalValueFinal: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#00A86B',
    width: 100,
    textAlign: 'right',
  },
  paymentBox: {
    marginTop: 15,
    marginBottom: 20,
    padding: 10,
    backgroundColor: '#F0FDF4',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#BBF7D0',
    alignSelf: 'flex-start',
    width: '40%',
  },
  paymentTitle: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#166534',
    marginBottom: 4,
  },
  paymentText: {
    fontSize: 8,
    color: '#166534',
    marginBottom: 1,
  },
  balanceBox: {
    marginTop: 15,
    marginBottom: 20,
    padding: 10,
    backgroundColor: '#FFF7ED',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#FED7AA',
    alignSelf: 'flex-start',
    width: '40%',
  },
  balanceTitle: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#9A3412',
    marginBottom: 4,
  },
  balanceText: {
    fontSize: 8,
    color: '#9A3412',
    marginBottom: 1,
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 30,
    right: 30,
    textAlign: 'center',
    borderTopWidth: 1,
    borderTopColor: '#E8ECEF',
    paddingTop: 10,
  },
  footerText: {
    fontSize: 7,
    color: '#999999',
    textAlign: 'center',
    marginBottom: 2,
  },
});

// ============================================
// HELPERS
// ============================================
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return 'R 0.00';
  return `R ${parseFloat(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getStatusLabel = (status) => {
  const labels = {
    'proforma': 'PROFORMA',
    'tax_invoice': 'TAX INVOICE',
    'sent': 'SENT',
    'pending': 'PENDING',
    'partially_paid': 'PARTIALLY PAID',
    'paid': 'PAID',
    'overdue': 'OVERDUE',
    'cancelled': 'CANCELLED',
    'void': 'VOIDED'
  };
  return labels[status] || (status || '').toUpperCase();
};

// Payment schedule lines for the locked terms list. Returns
// [{ label, amount }] describing when each portion falls due.
const paymentScheduleLines = (term, total) => {
  const amount = (pct) => formatCurrency(total * (pct / 100));
  switch (term) {
    case 'Due on Receipt':
    case 'Pay in Advance':
    case 'Pay in Advance (Prepaid)':
      return [{ label: '100% payable on receipt', amount: amount(100) }];
    case '50% Booking/50% EPOD':
      return [
        { label: '50% on booking', amount: amount(50) },
        { label: '50% on proof of delivery (EPOD)', amount: amount(50) }
      ];
    case '50% Booking/50% Completion':
      return [
        { label: '50% on booking', amount: amount(50) },
        { label: '50% on completion', amount: amount(50) }
      ];
    case 'COD':
      return [{ label: 'Payable on delivery (COD)', amount: amount(100) }];
    default: {
      const days = { '7 Days': 7, '14 Days': 14, '15 Days': 15, '30 Days': 30 }[term] || 30;
      return [{ label: `100% payable ${days} days after job completion`, amount: amount(100) }];
    }
  }
};


// ============================================
// MAIN COMPONENT
// ============================================
const ClientInvoicePDF = ({ invoice, job, quote, payments, company }) => {
  const companyObj = company || {};
  const fullAddress = [companyObj.company_address || DEFAULT_COMPANY.company_address, companyObj.company_address_line2 || DEFAULT_COMPANY.company_address_line2].filter(Boolean).join(', ');
  const bank = {
    accountName: companyObj.bank_account_name || DEFAULT_COMPANY.bank_account_name,
    bank: companyObj.bank_name || DEFAULT_COMPANY.bank_name,
    accountNumber: companyObj.bank_account_number || DEFAULT_COMPANY.bank_account_number,
    accountType: companyObj.bank_account_type || DEFAULT_COMPANY.bank_account_type,
    branchCode: companyObj.bank_branch_code || DEFAULT_COMPANY.bank_branch_code,
  };

  const resolveLogo = (logo) => {
    if (!logo) return null;
    if (logo.startsWith('http://') || logo.startsWith('https://') || logo.startsWith('data:')) return logo;
    if (typeof window !== 'undefined') return window.location.origin + logo;
    return logo;
  };
  const logoSrc = resolveLogo(companyObj.company_logo);

  if (!invoice) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <Text style={styles.quoteTitle}>No Invoice Data</Text>
        </Page>
      </Document>
    );
  }

  const subtotal = parseFloat(invoice.subtotal || 0);
  const vatAmount = parseFloat(invoice.vat_amount || 0);
  const total = parseFloat(invoice.total_amount || (subtotal + vatAmount));
  const paid = parseFloat(invoice.paid_amount || 0);
  const creditApplied = parseFloat(invoice.credit_applied || 0);
  // Voided/cancelled/credited invoices are not collectable, so the balance is R0 — see
  // utils/invoiceBalance.js. isVoided also stops a voided invoice printing a payable schedule.
  const balance = invoiceBalance(invoice);
  const isVoided = isInvoiceVoided(invoice);
  const isPaid = invoice.status === 'paid';
  const isPartiallyPaid = !isPaid && (invoice.status === 'partially_paid' || paid > 0 || creditApplied > 0);
  // Settled purely by a credit note (no cash received) — word it honestly.
  const settledByCredit = creditApplied > 0 && balance <= 0.009 && paid <= 0.009;

  const client = invoice.client || invoice.clients || null;

  // `quantity_mt` is a QUANTITY, not a weight. The app stores 1 for one load, one night's standing
  // time, even a discount line — and measured across every line in the system it is never anything
  // but 1 (or blank). So it must never be labelled MT: "1 MT" beside a R48,840 haulage line told
  // the client they had bought one tonne. The job's own tonnage is shown where it is genuinely
  // known — in the fallback below, for a job that really is measured in tonnes.
  const units = jobUnits(job);
  const qtyLabel = (q) => {
    if (q == null || q === '') return '-';
    const n = Number(q);
    return Number.isFinite(n) ? String(n) : '-';
  };

  const lineItems = (invoice.items && invoice.items.length > 0 ? invoice.items : []).map((item) => ({
    description: item.description || 'Transport Services',
    qty: qtyLabel(item.quantity_mt),
    rate: formatCurrency(item.rate_per_mt || item.amount || 0),
    amount: formatCurrency(item.amount || 0),
  }));

  if (lineItems.length === 0) {
    lineItems.push({
      description: jobCommodity || invoice.notes || 'Transport Services',
      qty: units.kind === 'mt' && jobTotalMt ? `${jobTotalMt} MT` : '-',
      rate: formatCurrency(subtotal),
      amount: formatCurrency(subtotal),
    });
  }

  // The Job Details block should read from the `job` prop when it is supplied, and from the
  // invoice's own embedded job row otherwise. Decided PER FIELD rather than per object: a caller
  // that passes a job carrying only some of the fields used to blank the rest of the block.
  const embeddedJob = (invoice && invoice.jobs) || {};
  const providedJob = (job && typeof job === 'object') ? job : {};
  const jobField = (key) => providedJob[key] || embeddedJob[key] || null;

  const jobNumber = jobField('job_number');
  const jobDescription = jobField('job_description');
  const jobCommodity = jobField('commodity');
  const jobOrigin = jobField('origin');
  const jobDestination = jobField('destination');

  const poRef = jobField('loadcon_ref_number');
  const jobTotalMt = jobField('total_quantity_mt');
  const quoteNumber = quote?.quote_number || (invoice.quotes && invoice.quotes.quote_number) || null;
  // The client's own Purchase Order (captured when they accepted the quote). Read it from
  // the job first, then the quote - whichever the API payload happens to carry.
  // These three were read only from the `job` prop, which the callers do not always supply — so the
  // Client P.O. and the accepted terms silently vanished from invoices that had them.
  const clientPoNumber = jobField('purchase_order_number') || quote?.purchase_order_number || null;
  const termsAcceptedAt = jobField('terms_accepted_at') || quote?.terms_accepted_at || null;
  const termsAcceptedName = jobField('terms_accepted_name') || quote?.terms_accepted_name || null;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* HEADER - same as proforma */}
        <View style={styles.header}>
          <View style={styles.headerContent}>
            <View style={styles.headerLeft}>
              {logoSrc && <Image src={logoSrc} style={styles.headerLogo} />}
            </View>
            <View style={styles.headerDetails}>
              {fullAddress && <Text style={styles.headerDetailText}>{fullAddress}</Text>}
              {companyObj.company_phone && <Text style={styles.headerDetailText}>Tel: {companyObj.company_phone}</Text>}
              {companyObj.company_email && <Text style={styles.headerDetailText}>Email: {companyObj.company_email}</Text>}
              {companyObj.company_website && <Text style={styles.headerDetailText}>Web: {companyObj.company_website}</Text>}
              {companyObj.company_reg_number && <Text style={styles.headerDetailText}>Reg: {companyObj.company_reg_number}</Text>}
              {companyObj.company_vat_number && <Text style={styles.headerDetailText}>VAT: {companyObj.company_vat_number}</Text>}
            </View>
          </View>
        </View>

        <View style={styles.content}>
          <Text style={styles.quoteTitle}>TAX INVOICE</Text>

          {/* Info rows — references block */}
          <View style={styles.infoRow}>
            <View style={styles.infoItem}><Text style={styles.infoLabel}>Invoice Number</Text><Text style={styles.infoValue}>{invoice.invoice_number || 'N/A'}</Text></View>
            {quoteNumber && <View style={styles.infoItem}><Text style={styles.infoLabel}>Quote Number</Text><Text style={styles.infoValue}>{quoteNumber}</Text></View>}
            <View style={styles.infoItem}><Text style={styles.infoLabel}>Job Number</Text><Text style={styles.infoValue}>{job?.job_number || invoice.jobs?.job_number || 'N/A'}</Text></View>
            {clientPoNumber && <View style={styles.infoItem}><Text style={styles.infoLabel}>Client P.O.</Text><Text style={styles.infoValue}>{clientPoNumber}</Text></View>}
            <View style={styles.infoItem}><Text style={styles.infoLabel}>Proforma Number</Text><Text style={styles.infoValue}>{invoice.proforma_number || 'N/A'}</Text></View>
          </View>
          <View style={styles.infoRow}>
            <View style={styles.infoItem}><Text style={styles.infoLabel}>Invoice Date</Text><Text style={styles.infoValue}>{formatDate(invoice.invoice_date || invoice.created_at)}</Text></View>
            <View style={styles.infoItem}><Text style={styles.infoLabel}>Due Date</Text><Text style={styles.infoValue}>{formatDate(invoice.due_date)}</Text></View>
            <View style={styles.infoItem}><Text style={styles.infoLabel}>Payment Terms</Text><Text style={styles.infoValue}>{invoice.payment_terms || '30 Days'}</Text></View>
            <View style={styles.infoItem}>
              <Text style={styles.infoLabel}>Status</Text>
              <Text style={isPaid ? styles.infoValuePaid : styles.infoValue}>{getStatusLabel(invoice.status)}</Text>
            </View>
          </View>


          <View style={styles.divider} />

          <View style={styles.twoCol}>
            <View style={styles.col}>
              <Text style={styles.sectionTitle}>Bill To</Text>
              <Text style={styles.sectionText}>{client?.company_name || 'N/A'}</Text>
              {client?.contact_person && <Text style={styles.sectionText}>Attn: {client.contact_person}</Text>}
              {client?.contact_email && <Text style={styles.sectionText}>Email: {client.contact_email}</Text>}
              {client?.physical_address && <Text style={styles.sectionText}>{client.physical_address}</Text>}
              {client?.registration_number && <Text style={styles.sectionText}>Reg: {client.registration_number}</Text>}
              {client?.vat_number && <Text style={styles.sectionText}>VAT: {client.vat_number}</Text>}
            </View>
            <View style={styles.col}>
              <Text style={styles.sectionTitle}>Job Details</Text>
              <Text style={styles.sectionText}>Job: {jobNumber || 'N/A'}</Text>
              {/* The job's own description — the instruction for the work as the customer gave it.
                  It is multi-line, so it is printed as it stands. */}
              {/* The job description has its own full-width section below. */}
              {/* Commodity is often the very same text as the description above (it is how these jobs
                  are captured), so it is only shown when it actually adds something. */}
              {jobCommodity && jobCommodity !== jobDescription && <Text style={styles.sectionText}>Commodity: {jobCommodity}</Text>}
              {/* Origin and destination appear in the Route Information block below. */}
              {clientPoNumber && <Text style={styles.sectionText}>Client P.O.: {clientPoNumber}</Text>}
              {termsAcceptedAt && <Text style={styles.sectionText}>Terms accepted {formatDate(termsAcceptedAt)}{termsAcceptedName ? ` by ${termsAcceptedName}` : ''}</Text>}
              {poRef && <Text style={styles.sectionText}>PO Ref: {poRef}</Text>}
            </View>
          </View>

          {/* Route Information — the same block the quote and proforma show, so an invoice reads
              like the document it came from rather than cramming the route into a narrow column. */}
          {(jobOrigin || jobDestination) && (
            <>
              <View style={styles.divider} />
              <View style={styles.routeBox}>
                <Text style={[styles.sectionTitle, { color: '#0A1929' }]}>Route Information</Text>
                <View style={{ flexDirection: 'row', gap: 20 }}>
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 8, color: '#666666', marginBottom: 2 }}>Pickup</Text>
                    <Text style={styles.routeText}>{jobOrigin || 'N/A'}</Text>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 8, color: '#666666', marginBottom: 2 }}>Delivery</Text>
                    <Text style={styles.routeText}>{jobDestination || 'N/A'}</Text>
                  </View>
                </View>
              </View>
            </>
          )}

          {/* Job Description — full width, exactly as the quote presents it. */}
          {jobDescription && (
            <>
              <View style={styles.divider} />
              <View>
                <Text style={styles.sectionTitle}>Job Description</Text>
                <Text style={styles.sectionText}>{jobDescription}</Text>
              </View>
            </>
          )}

          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableHeaderText, styles.colDescription]}>Description</Text>
              <Text style={[styles.tableHeaderText, styles.colQty]}>Qty</Text>
              <Text style={[styles.tableHeaderText, styles.colRate]}>Rate</Text>
              <Text style={[styles.tableHeaderText, styles.colAmount]}>Amount</Text>
            </View>
            {lineItems.map((item, index) => (
              <View key={index} style={index % 2 === 0 ? styles.tableRow : styles.tableRowEven} wrap={false}>
                <Text style={[styles.tableText, styles.colDescription]}>{item.description}</Text>
                <Text style={[styles.tableText, styles.colQty]}>{item.qty}</Text>
                <Text style={[styles.tableText, styles.colRate]}>{item.rate}</Text>
                <Text style={[styles.tableText, styles.colAmount]}>{item.amount}</Text>
              </View>
            ))}
          </View>


          {/* Bank details + Totals */}
          <View style={{ flexDirection: 'row', gap: 20, marginTop: 10 }} wrap={false}>
            <View style={styles.paymentBox}>
              <Text style={styles.paymentTitle}>Payment Details</Text>
              <Text style={styles.paymentText}>Account: {bank.accountName}</Text>
              <Text style={styles.paymentText}>Bank: {bank.bank}</Text>
              <Text style={styles.paymentText}>Acc No: {bank.accountNumber}</Text>
              <Text style={styles.paymentText}>Ref: {invoice.invoice_number || 'N/A'}</Text>
            </View>
            <View style={[styles.totalsBox, { flex: 1 }]}>
              <View style={styles.totalRow}><Text style={styles.totalLabel}>Subtotal</Text><Text style={styles.totalValue}>{formatCurrency(subtotal)}</Text></View>
              {vatAmount > 0 && <View style={styles.totalRow}><Text style={styles.totalLabel}>VAT (15%)</Text><Text style={styles.totalValue}>{formatCurrency(vatAmount)}</Text></View>}
              <View style={styles.totalRowFinal}><Text style={styles.totalLabelFinal}>TOTAL</Text><Text style={styles.totalValueFinal}>{formatCurrency(total)}</Text></View>
              {/* Credits / payments / balance are shown in the Outstanding Balance
                  box below — deliberately NOT repeated under the TOTAL, so the
                  tax invoice reads as an official document. */}
            </View>
          </View>

          {/* Payment schedule — split by payment terms. Never on a voided invoice: asking for
              payment on a cancelled document is exactly the kind of thing that confuses a client. */}
          {!isPaid && !isVoided && (
            <View style={{ marginTop: 12, padding: 10, backgroundColor: '#F8FAFC', borderRadius: 4 }}>
              <Text style={styles.paymentTitle}>Payment Schedule</Text>
              <Text style={styles.paymentText}>Terms: {invoice.payment_terms || '30 Days'}</Text>
              {paymentScheduleLines(invoice.payment_terms, total).map((line, i) => (
                <Text key={i} style={styles.paymentText}>{line.label}: {line.amount}</Text>
              ))}
            </View>
          )}

          {/* Payment confirmation / balance notice */}
          {isPaid && (
            <View style={styles.paymentBox}>
              <Text style={styles.paymentTitle}>{settledByCredit ? '✅ Settled by Credit Note' : '✅ Payment Confirmed'}</Text>
              {paid > 0 && <Text style={styles.paymentText}>Paid on: {formatDate(invoice.payment_date)}</Text>}
              {paid > 0 && <Text style={styles.paymentText}>Method: {invoice.payment_method || 'Bank Transfer'}</Text>}
              {creditApplied > 0 && <Text style={styles.paymentText}>Credit applied: {formatCurrency(creditApplied)}</Text>}
              {invoice.pop_url && <Text style={styles.paymentText}>POP Ref: {invoice.pop_url.split('/').pop() || 'N/A'}</Text>}
            </View>
          )}
          {isPartiallyPaid && !isPaid && (
            <View style={styles.balanceBox}>
              <Text style={styles.balanceTitle}>Outstanding Balance</Text>
              {creditApplied > 0 && <Text style={styles.balanceText}>Credit applied: {formatCurrency(creditApplied)}</Text>}
              <Text style={styles.balanceText}>Paid: {formatCurrency(paid)}</Text>
              <Text style={styles.balanceText}>Balance Due: {formatCurrency(balance)}</Text>
              <Text style={styles.balanceText}>Due: {formatDate(invoice.due_date)}</Text>
            </View>
          )}
        </View>

        {/* FOOTER - same as proforma + payment terms only */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>{companyObj.company_name || 'One Purpose Logistics'} │ {fullAddress} │ Tel: {companyObj.company_phone}</Text>
          <Text style={[styles.footerText, { marginTop: 3 }]}>
            Reg: {companyObj.company_reg_number}{companyObj.company_vat_number ? ` │ VAT: ${companyObj.company_vat_number}` : ''} │ Terms: {invoice.payment_terms || '30 Days'}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default ClientInvoicePDF;
